import React, { useState } from "react";
import { useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import { useGetNcsAuditQuery } from "../../../../store/appApi";
import { panelClass, fieldClass, btnGhost } from "./config";

const ACTIONS = [
  "followup.assign",
  "followup.call",
  "followup.mark-paid",
  "modem-recovery.transfer",
  "modem-recovery.visit",
  "modem-recovery.outcome",
  "modem-recovery.handover",
  "modem-recovery.bulk-handover",
  "modem-recovery.auto-recovered",
  "modem-recovery.remark",
  "modem-recovery.reopen",
  "team.add",
  "team.remove",
  "sheet.upload",
  "run.rerun-diff",
];

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const NcsAuditLog = () => {
  const role = useSelector(selectUserRole);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const { data, isFetching } = useGetNcsAuditQuery(
    { action, from, to, customerKey: q.trim(), page, limit: 100 },
    { skip: role !== "super-admin" },
  );
  const logs = data?.logs || [];
  const totalPages = data?.totalPages || 1;

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Audit Log" motherMenu="Netplus" />
        <div className={`${panelClass} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Audit Log" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <select
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All actions</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <input
            type="date"
            className={`${fieldClass} w-40`}
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
          <input
            type="date"
            className={`${fieldClass} w-40`}
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
          <input
            className={`${fieldClass} w-44`}
            placeholder="Customer key"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className={btnGhost} onClick={() => setPage(1)}>
            Apply
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">Who</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Summary</th>
                <th className="px-4 py-3 text-left">Customer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                    No audit entries.
                  </td>
                </tr>
              ) : (
                logs.map((l) => (
                  <tr key={l._id} className="hover:bg-cyan-50/30">
                    <td className="px-4 py-2 text-gray-500">{fmtDateTime(l.at)}</td>
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-800">{l.actorName || "—"}</span>
                      <span className="ml-1 text-xs text-gray-400">{l.actorRole}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-cyan-700">{l.action}</td>
                    <td className="px-4 py-2 text-gray-600">{l.summary}</td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {l.customerKey || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button className={btnGhost} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default NcsAuditLog;
